import React, { useEffect, useState } from 'react'
import { Modal, Checkbox, Table, Radio, Dropdown } from 'semantic-ui-react'
import { CustomerUserService } from 'utility/services/customer-user'
import { isRoleModelOn } from 'utility/role-model'
import { useAuth } from 'context/user'
import { useNotification } from 'hooks/toast'
import { LoaderBar } from 'components'
import styled from 'styled-components'
import { X } from 'phosphor-react'
import { SubmitCancelButtons } from 'elements/Button'

const ScrollableContent = styled(Modal.Content)`
    max-height: 60vh;
    overflow-y: auto;

    .ui.table {
        border: none;
        margin-top: 0;
    }
`

const ReadOnlyCell = styled(Table.Cell)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    text-align: right;
    width: 120px;
    padding-right: 14px !important;
`

// Department role model: what the user is for each assigned department. 'default' = the role decides (a Department
// user is head of department, everyone else audits).
const RELATION_OPTIONS = [
    { key: 'default', value: 'default', text: 'By role' },
    { key: 'own', value: 'own', text: 'Head of department' },
    { key: 'audit', value: 'audit', text: 'Audits' },
]

const relationFromRow = row => {
    if (row?.IsOwner === true) return 'own'
    if (row?.IsOwner === false) return 'audit'
    return 'default'
}

export const CustomerAssignModal = ({ open, onClose, userId }) => {
    const { user: currentUser } = useAuth()
    const { errorToast } = useNotification()
    // only for companies on the department role model (the server answers 400 ROLE_MODEL_OFF for the others)
    const roleModel = isRoleModelOn(currentUser)
    const [customers, setCustomers] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectAll, setSelectAll] = useState(false)
    const [relations, setRelations] = useState({})
    const [loadedRelations, setLoadedRelations] = useState({})
    // the column is shown (and saved) only when the current marks were loaded - otherwise a "By role" choice could not
    // be told apart from "not loaded"
    const [relationsLoaded, setRelationsLoaded] = useState(false)
    const showRelations = roleModel && relationsLoaded

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setCustomers([])
            setSelectAll(false)
            setRelations({})
            setLoadedRelations({})
            setRelationsLoaded(false)
        }
    }, [open])

    // Fetch customers when modal opens
    useEffect(() => {
        if (open && userId) {
            fetchCustomers()
        }
    }, [open, userId])

    const fetchCustomers = async () => {
        try {
            setLoading(true)
            const response = await CustomerUserService.getByUserId(userId)
            // Ensure we're not duplicating entries by using CustomerId as unique identifier
            const uniqueCustomers = response.reduce((acc, customer) => {
                if (!acc.find(c => c.CustomerId === customer.CustomerId)) {
                    // Prioritize IsReadOnly from API response, fallback to parsing if needed
                    let isReadOnly = false
                    let actualCustomerId = customer.CustomerId

                    // First check if IsReadOnly is directly available in the response
                    if (customer.IsReadOnly !== undefined) {
                        isReadOnly = customer.IsReadOnly === true
                    } else if (customer.CustomerId && typeof customer.CustomerId === 'string' && customer.CustomerId.includes('-')) {
                        // Fallback: Parse IsReadOnly from CustomerId format "Id-0" or "Id-1"
                        // Mapping: 0 = IsReadOnly false, 1 = IsReadOnly true
                        const parts = customer.CustomerId.split('-')
                        if (parts.length === 2) {
                            const [id, readonlyFlag] = parts
                            isReadOnly = readonlyFlag === '1'
                            actualCustomerId = id // Extract actual ID
                        }
                    }

                    acc.push({
                        ...customer,
                        CustomerId: actualCustomerId,
                        IsReadOnly: isReadOnly,
                    })
                }
                return acc
            }, [])
            setCustomers(uniqueCustomers)
            if (roleModel) {
                try {
                    const rel = await CustomerUserService.getDepartmentRelation(userId)
                    const map = {}
                    const relItems = rel?.Items || []
                    relItems.forEach(item => {
                        map[String(item.CustomerId)] = relationFromRow(item)
                    })
                    setRelations(map)
                    setLoadedRelations(map)
                    setRelationsLoaded(true)
                } catch {
                    // no column; the department save still works
                    setRelationsLoaded(false)
                }
            }
            // Check if all customers are selected
            const allSelected = uniqueCustomers.every(
                customer => customer.IsSelected,
            )
            setSelectAll(allSelected)
        } catch (error) {
            console.error('Error fetching customers:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSelectAll = () => {
        const newSelectAll = !selectAll
        setSelectAll(newSelectAll)
        setCustomers(
            customers.map(customer => ({
                ...customer,
                IsSelected: newSelectAll,
            })),
        )
    }

    const handleReadOnlyToggle = customerId => {
        setCustomers(
            customers.map(customer =>
                customer.CustomerId === customerId
                    ? { ...customer, IsReadOnly: !customer.IsReadOnly }
                    : customer,
            ),
        )
    }

    const handleCustomerSelect = customerId => {
        const updatedCustomers = customers.map(customer =>
            customer.CustomerId === customerId
                ? {
                      ...customer,
                      IsSelected: !customer.IsSelected,
                      IsReadOnly: !customer.IsSelected ? false : customer.IsReadOnly, // Reset IsReadOnly when deselected
                  }
                : customer,
        )
        setCustomers(updatedCustomers)
        setSelectAll(updatedCustomers.every(customer => customer.IsSelected))
    }

    const handleSave = async () => {
        try {
            setLoading(true)
            // Format selected customer IDs as "Id-0" (not readonly) or "Id-1" (readonly)
            // Mapping: IsReadOnly false → send 0, IsReadOnly true → send 1
            // Example: "2286-0" means IsReadOnly=false, "2313-1" means IsReadOnly=true
            const selectedCustomerIds = customers
                .filter(customer => customer.IsSelected)
                .map(customer => {
                    const readonlyFlag = customer.IsReadOnly === true ? '1' : '0'
                    return `${customer.CustomerId}-${readonlyFlag}`
                })
                .join(';')

            const unselectedCustomerIds = customers
                .filter(customer => !customer.IsSelected)
                .map(customer => customer.CustomerId)
                .join(';')

            const payload = {
                UserId: userId,
                CustomerIds: selectedCustomerIds,
                UnselectedCustomerIds: unselectedCustomerIds,
            }

            // If there are any customers in the list, use update (edit mode)
            // Otherwise, use create (new assignment or all unchecked)
            if (customers.some(c => c.IsSelected || !c.IsSelected)) {
                await CustomerUserService.update(payload)
            } else {
                await CustomerUserService.create(payload)
            }
            if (showRelations) {
                // head of department / audits marks of the selected departments that changed (the assignment rows exist
                // now; the server keeps the marks when the departments are saved again)
                const items = customers
                    .filter(customer => customer.IsSelected)
                    .map(customer => String(customer.CustomerId))
                    .filter(id => (relations[id] || 'default') !== (loadedRelations[id] || 'default'))
                    .map(id => ({ CustomerId: Number(id), Relation: relations[id] || 'default' }))
                if (items.length) {
                    try {
                        await CustomerUserService.saveDepartmentRelation({ UserId: userId, Items: items })
                    } catch (relError) {
                        if (!relError?.roleRefusalHandled) {
                            errorToast(
                                relError?.error?.Message ||
                                    'Departments saved, but head of department / audits could not be saved.',
                            )
                        }
                        return
                    }
                }
            }
            onClose(true)
        } catch (error) {
            console.error('Error saving customer assignments:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setCustomers([])
        setSelectAll(false)
        onClose(false)
    }

    return (
        <Modal open={open} onClose={handleClose} size={showRelations ? 'large' : 'small'}>
            <Modal.Header>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{customers.some(c => c.IsSelected) ? 'Edit Assign Customer' : 'Assign Customer'}</span>
                    <X className="pointer" size={32} onClick={handleClose} />
                </div>
            </Modal.Header>
            <ScrollableContent>
                {loading && <LoaderBar content="Loading" />}
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    label="Select All"
                                />
                            </Table.HeaderCell>
                            <Table.HeaderCell>Customer Name</Table.HeaderCell>
                            {showRelations && (
                                <Table.HeaderCell style={{ width: '190px' }}>
                                    Role in department
                                </Table.HeaderCell>
                            )}
                            <Table.HeaderCell textAlign="right" style={{ width: '120px', paddingRight: '14px' }}>IsReadOnly</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {customers.map(customer => (
                            <Table.Row key={customer.CustomerId}>
                                <Table.Cell collapsing>
                                    <Checkbox
                                        checked={customer.IsSelected}
                                        onChange={() =>
                                            handleCustomerSelect(
                                                customer.CustomerId,
                                            )
                                        }
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    {customer.CustomerName ||
                                        'Unnamed Customer'}
                                </Table.Cell>
                                {showRelations && (
                                    <Table.Cell>
                                        <Dropdown
                                            selection
                                            compact
                                            fluid
                                            options={RELATION_OPTIONS}
                                            value={
                                                relations[String(customer.CustomerId)] ||
                                                'default'
                                            }
                                            disabled={!customer.IsSelected}
                                            onChange={(_, { value }) =>
                                                setRelations(prev => ({
                                                    ...prev,
                                                    [String(customer.CustomerId)]: value,
                                                }))
                                            }
                                        />
                                    </Table.Cell>
                                )}
                                <ReadOnlyCell>
                                    <Radio
                                        toggle
                                        checked={customer.IsReadOnly === true}
                                        onChange={() =>
                                            handleReadOnlyToggle(
                                                customer.CustomerId,
                                            )
                                        }
                                        disabled={!customer.IsSelected}
                                    />
                                </ReadOnlyCell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </ScrollableContent>
            <Modal.Actions>
                <SubmitCancelButtons
                    loading={loading}
                    onSubmit={handleSave}
                    onCancel={handleClose}
                    hideCancel={false}
                    cancelText="Cancel"
                />
            </Modal.Actions>
        </Modal>
    )
}
